import { Component, computed, inject, input } from '@angular/core';
import { of } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TableModule } from '@openng/optimus-ui/table';
import { MessageService } from '@openng/optimus-ui/api';

import { rxResourceFixed } from '@zayan-erp/shared';
import { DsmApsService } from '../../services/dsm-aps.service';
import { DsmDecorConsultantService } from '../../services/dsm-decor-consultant.service';
import { DsmDecorConsultant } from '../../models/dsm-decor-consultant.models';

interface DecorConsultantRow {
  id: string;
  fullName: string;
  email: string;
  isAssigned: boolean;
  searchIndex: string;
}

@Component({
  selector: 'dsm-aps-decor-consultant',
  imports: [ButtonModule, TableModule],
  templateUrl: './dsm-aps-decor-consultant.component.html'
})
export class DsmApsDecorConsultantComponent {
  dealId = input.required<string>();
  searchTerm = input('');

  readonly #dsmApsService = inject(DsmApsService);
  readonly #decorConsultantService = inject(DsmDecorConsultantService);
  readonly #messageService = inject(MessageService);

  readonly dealResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getDealHeader(params.dealId);
    }
  });

  readonly effectiveLotId = computed(() => this.dealResource.value()?.lot?.id ?? '');

  readonly consultantsResource = rxResourceFixed({
    params: () => ({}),
    defaultValue: [] as DsmDecorConsultant[],
    stream: () => this.#decorConsultantService.readAvailableConsultants()
  });

  readonly assignedResource = rxResourceFixed<DsmDecorConsultant | null, { lotId: string }>({
    params: () => ({ lotId: this.effectiveLotId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.lotId) return of(null);
      return this.#decorConsultantService.readLotConsultant(params.lotId);
    }
  });

  readonly assignedConsultantId = computed(() => this.assignedResource.value()?.id ?? null);

  readonly #rows = computed<DecorConsultantRow[]>(() => {
    const assignedId = this.assignedConsultantId();
    return (this.consultantsResource.value() ?? []).map(consultant => ({
      id: consultant.id,
      fullName: consultant.fullName,
      email: consultant.email,
      isAssigned: consultant.id === assignedId,
      searchIndex: `${consultant.fullName} ${consultant.email}`.toLowerCase()
    }));
  });

  readonly rows = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.#rows();
    return this.#rows().filter(row => row.searchIndex.includes(term));
  });

  readonly isLoading = computed(() => this.consultantsResource.isLoading() || this.assignedResource.isLoading());

  assign(row: DecorConsultantRow): void {
    const lotId = this.effectiveLotId();
    if (!lotId) return;

    if (this.assignedConsultantId()) {
      this.#messageService.add({
        severity: 'error',
        summary: 'Consultant already assigned. Please unassign the consultant first.'
      });
      return;
    }

    this.#decorConsultantService.assignConsultant(lotId, row.id).subscribe(() => {
      this.assignedResource.reload();
    });
  }

  unassign(): void {
    const lotId = this.effectiveLotId();
    if (!lotId) return;

    this.#decorConsultantService.unassignConsultant(lotId).subscribe(() => {
      this.assignedResource.reload();
    });
  }
}
